// src/components/TheFarm.js
import React, { useEffect, useRef, useState } from 'react'
import {
  Container,
  Row,
  Col,
  Accordion,
  Alert,
} from 'react-bootstrap'
import { useNavigate, useLocation } from 'react-router-dom'
 
import FloatingActionButton from './FloatingActionButton'
import { useContent } from '../hooks/useContent'
import './TheFarm.css'
import Footer from './footer'
import heroImg from '../assets/imgDisp/farmHero.jpg'
import organic from '../assets/imgDisp/org.jpg'
import chicken from '../assets/imgDisp/chicken.jpg'
import dairy from '../assets/imgDisp/dairy.jpg'
import bees from '../assets/imgDisp/bees.jpeg'
import biogas from '../assets/imgDisp/bioGas.png'
import solar from '../assets/imgDisp/solar.jpeg'
import shola from '../assets/imgDisp/shola.jpg'
import waterl from '../assets/imgDisp/waterL.jpg'
import waters from '../assets/imgDisp/waterS.jpg'
import zero from '../assets/imgDisp/zero.jpg'
import cow from '../assets/imgDisp/cow.jpeg'



const TheFarm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { content, loading, error } = useContent('theFarm')

  // Lazy background helper: apply background-image only when near viewport
  const LazyBackground = ({ className, src, style, children }) => {
    const ref = useRef(null)
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
      const el = ref.current
      if (!el || !src) {
        setLoaded(true)
        return
      }
      if (typeof IntersectionObserver === 'undefined') {
        setLoaded(true)
        return
      }
      const io = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              setLoaded(true)
              io.disconnect()
              break
            }
          }
        },
        { rootMargin: '200px 0px', threshold: 0.01 }
      )
      io.observe(el)
      return () => io.disconnect()
    }, [src])

    const bg = loaded && src ? { backgroundImage: `url(${src})` } : {}
    return (
      <div ref={ref} className={className} style={{ ...(style || {}), ...bg }}>
        {children}
      </div>
    )
  }

  // Extract content with fallbacks
  const heroContent = content?.hero || {
    title: 'Sustainable Living and Responsible Living Spaces',
    subtitle: 'Rooted, Resilient, Regenerative',
    backgroundImage: '',
  }
  const gardenContent = content?.gardenSection || {
    title: 'Organic Gardens & Orchards',
    paragraphs: [],
    stats: [],
    image: '',
  }
  const animalsContent = content?.animals || {
    title: 'Animals We Love',
    intro: '',
    animalCards: [],
  }
  const systemsContent = content?.systems || {
    title: 'Our Self-Sustaining Systems',
    intro: '',
    accordionItems: [],
  }
  const cuisineContent = content?.cuisine || {
    title: 'Our Cuisine',
    paragraphs: [],
    features: [],
    image: '',
  }
  const ctaContent = content?.cta || {
    title: 'Experience Farm Life',
    description: '',
    primaryButtonText: 'Book a Farm Tour',
    secondaryButtonText: 'View Workshops',
  }
  // Set default placeholder images immediately (show local fallback right away)
  const [heroImage, setHeroImage] = useState(heroImg)
  const [heroLoaded, setHeroLoaded] = useState(true)
  const [gardenImage, setGardenImage] = useState(
    organic
  )
  const [animalImages, setAnimalImages] = useState([
    cow,
    chicken,
    dairy,
    bees,
  ])
  const [systemImages, setSystemImages] = useState([
    shola, waterl, waters, biogas, solar, zero

  ])

  

  useEffect(() => {
    // Preload hero and garden images; prefer Firebase first, then fallback
    let cancelled = false
    const load = (src, setter, onLoaded) => {
      const img = new Image()
      img.onload = () => {
        if (cancelled) return
        setter(src)
        if (onLoaded) onLoaded()
      }
      img.onerror = () => {
        if (cancelled) return
        if (src !== heroImg && setter === setHeroImage) {
          // Fallback to static only if Firebase hero fails
          load(heroImg, setHeroImage, () => setHeroLoaded(true))
        } else if (src !== organic && setter === setGardenImage) {
          load(organic, setGardenImage)
        } else {
          setter(src)
          if (onLoaded) onLoaded()
        }
      }
      img.src = src
    }

    // Keep showing the local fallback immediately; still preload remote hero if available

    const desiredHero = (heroContent?.backgroundImage || '').trim()
    const desiredGarden = (gardenContent?.image || '').trim()

    if (desiredHero) {
      // Load remote hero; when loaded it will replace the local fallback
      load(desiredHero, setHeroImage, () => setHeroLoaded(true))
    } else {
      // No Firebase hero provided; ensure local fallback is present
      setHeroImage(heroImg)
      setHeroLoaded(true)
    }

    if (desiredGarden) load(desiredGarden, setGardenImage)
    else load(organic, setGardenImage)

    return () => { cancelled = true }
  }, [loading, heroContent?.backgroundImage, gardenContent?.image])

  

  // Handle section navigation from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const targetSection = urlParams.get('section')

    if (targetSection && targetSection !== 'undefined') {
      setTimeout(() => {
        const element = document.getElementById(targetSection)
        if (element) {
          const yOffset = -100
          const rect = element.getBoundingClientRect()
          const scrollPosition = window.pageYOffset + rect.top + yOffset
          window.scrollTo({ top: scrollPosition, behavior: 'smooth' })
        }
      }, 800)
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [location.search])

  // Removed loading spinner: render page immediately with fallback content.

  // Show error state
  if (error) {
    return (
      <div className='farm-page'>
        <Alert variant='danger' className='text-center mb-0'>
          <strong>Content Loading Error:</strong> {error}
          <p className='mt-2'>Please try refreshing the page.</p>
        </Alert>
      </div>
    )
  }

  // Helpers and dynamic data for Systems accordion
  const slugify = s => (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

  const defaultSystems = [
    {
      icon: '🌲',
      title: 'Preservation of shola-grassland ecosystem',
      category: 'Ecosystem Conservation',
      description:
        'The shola-grassland ecosystem is unique to the higher reaches of the southern Western Ghats. The shola-grassland ecosystem serves as the water tank for the peninsula. While the grasslands let the rainwater flow away, it flows through the shola into the streams. Efforts to conserve the shola started a decade back as a conscious effort by the promoters and today through this shola patch flows a stream that feeds the water reservoir that supplies the needs of the resort.',
      image: shola,
    },
    {
      icon: '🌾',
      title: 'Re-establishment of grassland and wetland habitat',
      category: 'Habitat Restoration',
      description:
        'Over the last many years, we have been working on conserving wetlands and preservation and expansion of grasslands, Wetlands are seen as wastelands in most parts of the world, the wetlands are very crucial and important ecosystem.',
      image: waterl,
    },
    {
      icon: '💧',
      title: 'Watershed Management & Rainwater Harvesting',
      category: 'Water Conservation',
      description:
        'Sustainable water solutions start with effective watershed management. By conserving soil and capturing rainfall at the source, we ensure year-round water availability for communities, agriculture, and ecosystems. Our rainwater harvesting systems recharge groundwater, reduce dependency on external sources, and build climate resilience.',
      image: waters,
    },
    {
      icon: '🔥',
      title: 'Biogas Generation',
      category: 'Renewable Energy',
      description:
        'We transform organic waste into clean, renewable energy through biogas generation. This process not only reduces landfill waste and harmful emissions but also produces eco-friendly fuel for cooking, heating, and electricity. Biogas empowers communities with sustainable energy while promoting a circular economy.',
      image: biogas,
    },
    {
      icon: '☀',
      title: 'Off-Grid Power Solutions',
      category: 'Energy Independence',
      description:
        'Our sustainable power solutions bring electricity using solar and hybrid technologies. Designed for efficiency and independence, these systems provide continuous, affordable, and clean power — anytime, anywhere.',
      image: solar,
    },
    {
      icon: '♻',
      title: 'Zero Waste Management',
      category: 'Circular Economy',
      description:
        'We believe in turning waste into resources. Through zero waste management practices, we minimize landfill use by recycling, reusing, composting, and innovating sustainable disposal methods. Our approach reduces environmental impact, conserves natural resources, and helps move towards a cleaner, greener, and circular future.',
      image: zero,
    },
  ]

  const accordionItems = (systemsContent?.accordionItems && systemsContent.accordionItems.length > 0)
    ? systemsContent.accordionItems
    : defaultSystems

  const defaultIds = ['shola-grassland', 'grassland-wetland', 'rainwater-harvesting', 'biogas-generation', 'off-grid-power', 'zero-waste']

  return (
    <div className='farm-page' style={{ background: 'none', backgroundColor: 'transparent' }}>
      {/* Hero Section */}
    <section className={`hero-section ${heroLoaded ? 'image-loaded' : 'image-loading'}`}>
  {heroImage && (
    <img 
      src={heroImage} 
      alt="Hero" 
      className={`hero-image-tag ${heroLoaded ? 'is-loaded' : ''}`}
      loading='eager'
      fetchpriority='high'
      decoding='sync'
      onLoad={() => setHeroLoaded(true)}
    />
  )}
  <div className={`hero-overlay ${heroLoaded ? 'is-loaded' : ''}`}>
    <div className="hero-content">
      <h1 className="hero-title">{heroContent.title}</h1>
      <p className="hero-subtitle">{heroContent.subtitle}</p>
    </div>
  </div>
</section>



      <section className='systems-section py-5'>
        <Container>
          <div className='text-center mb-5'>
            <h2 className='section-head'>{systemsContent.title}</h2>
            <p className='lead mb-4'>
              {systemsContent.intro ||
                '"Man, whether civilized or savage, is a child of nature – he is not the master of nature". Caring for the surroundings and giving back to the nature to preserve its sustenance, we put significant efforts to maintain the tranquillity of the space and contribute to eco-restoration through the following'}
            </p>
          </div>

          <Accordion defaultActiveKey={accordionItems.map((_, i) => String(i))} alwaysOpen className='systems-accordion'>
            {accordionItems.map((item, idx) => {
              const title = item?.title || defaultSystems[idx]?.title || `System ${idx + 1}`
              const description = item?.description || defaultSystems[idx]?.description || ''
              const category = item?.category || defaultSystems[idx]?.category || ''
              const icon = item?.icon || defaultSystems[idx]?.icon || ''
              const imgSrc = item?.image || systemImages[idx] || defaultSystems[idx]?.image || zero
              const itemId = defaultIds[idx] || `system-${idx}`
              return (
                <Accordion.Item eventKey={String(idx)} id={itemId} key={idx}>
                  <Accordion.Header>
                    <div className='accordion-content'>
                      <div className='accordion-icon me-3'>{icon}</div>
                      <div className='accordion-text'>
                        <h3>{title}</h3>
                        {category ? (
                          <span className='category-badge'>{category}</span>
                        ) : null}
                      </div>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row className='align-items-center'>
                      <Col md={6} className={idx % 2 === 0 ? 'order-md-1' : 'order-md-2'}>
                        <p className='system-description'>{description}</p>
                        {Array.isArray(item?.benefits) && item.benefits.filter(Boolean).length > 0 ? (
                          <ul className='benefits-list'>
                            {item.benefits.filter(Boolean).map((benefit, bi) => (
                              <li key={bi}>{benefit}</li>
                            ))}
                          </ul>
                        ) : null}
                      </Col>
                      <Col md={6} className={idx % 2 === 0 ? 'order-md-2 mb-4 mb-md-0' : 'order-md-1 mb-4 mb-md-0'}>
                        <div className='simple-image-wrapper'>
                          <img
                            src={imgSrc}
                            alt={title}
                            className='simple-experience-img'
                            loading='lazy'
                            decoding='async'
                          />
                          <div className='simple-overlay'>
                            <div className='overlay-emoji'>{icon}</div>
                            <h4 className='overlay-heading'>{title}</h4>
                            {category ? (
                              <p className='overlay-badge'>{category}</p>
                            ) : null}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>
              )
            })}
          </Accordion>
        </Container>
      </section>

      {/* Call to Action */}
      <section className='sectionCta py-5 text-center'>
        <Container>
          <h2 className='section-head'>{ctaContent.title}</h2>
          <p className='cta-text mb-4'>
            {ctaContent.description ||
              'Join us for a farm tour, workshop, or stay in our eco-cottages'}
          </p>
          <button
            variant='primary'
            // size='lg'
            className='farm-book-btn'
            onClick={() => navigate('/stays')}
          >
            {ctaContent.primaryButtonText}
          </button>
          {/* <button
            variant='outline-light'
            size='lg'
            className='book-btn'
            onClick={() => navigate('/contact')}
          >
            {ctaContent.secondaryButtonText}
          </button> */}
        </Container>
      </section>

      {/* Footer */}
      <Footer />

      <FloatingActionButton />
    </div>
  )
}

export default TheFarm